import { useCallback, useRef, useState } from "react";
import { FlatNodeItem } from "@components/common/tree-view/Interfaces";
import { useLang } from "@hooks/useLang";
import { HVButton, HVIcon } from "@repo/HVComponents";
import useDetectOutsideClick from "@hooks/useDetectOutsideClick";
import { ObjectType } from "@queries/tanstack/analytics-filter/AnalyticsFilterQuery";
import { useLocationListByDeviceId } from "@hooks/analytics/useLocationListByDeviceId";
import _ from "lodash";
import ModalManager from "@utils/ModalManager";
import * as S from "./AnalyticsRuleLayer.styled";
import {
  AnalyticsRuleTree,
  MAX_RULE_IN_ONE_LOCATION_COUNT
} from "./AnalyticsRuleTree";
import { ICON } from "../../../constants/icons";

interface AnalyticsRuleLayerProps {
  onOK: () => void;
  defaultRules: FlatNodeItem[];
  onChecked: (checkedNodes: FlatNodeItem[]) => void;
  ruleCategory: ObjectType;
  analyticsChartType?: string;
}
export const AnalyticsRuleLayer = (props: AnalyticsRuleLayerProps) => {
  const { translate } = useLang();
  const rootRef = useRef(null);
  useDetectOutsideClick(rootRef, props.onOK);

  const [rulesChecked, setRulesChecked] = useState<FlatNodeItem[]>(
    props.defaultRules
  );
  const { toLocations } = useLocationListByDeviceId({ deviceIds: [] });

  const isCheckboxChanged = useRef(false);
  const handleRulesOK = useCallback(() => {
    const locations = toLocations(
      _.map(rulesChecked, (rule) => String(rule.value).split(":")[1])
    );
    const isExceedRuleInOneLocationCount =
      locations &&
      locations.length === 1 &&
      rulesChecked.length > MAX_RULE_IN_ONE_LOCATION_COUNT;

    if (isExceedRuleInOneLocationCount) {
      ModalManager.message(
        translate("error_max_rule_in_one_location_selected", {
          max: MAX_RULE_IN_ONE_LOCATION_COUNT
        }),
        {
          title: translate("system"),
          labelButton: translate("ok")
        }
      );
      return;
    }
    if (isCheckboxChanged.current) {
      props.onChecked(rulesChecked);
    }
    props.onOK();
  }, [props, rulesChecked]);

  const handleClearAll = () => {
    isCheckboxChanged.current = true;
    setRulesChecked([]);
  };

  return (
    <S.Container ref={rootRef}>
      <S.RuleWrap>
        <AnalyticsRuleTree
          ruleCategory={props.ruleCategory}
          rulesChecked={rulesChecked.map((node) => node.value)}
          onChecked={(node) => {
            isCheckboxChanged.current = true;
            setRulesChecked(node.filter((v) => !v.children));
          }}
          analyticsChartType={props.analyticsChartType}
        />
      </S.RuleWrap>
      <S.BottomWrap>
        <HVButton
          variant="Ghost"
          leftSection={<HVIcon name="refresh" svgProps={{ ...ICON.SIZE_16 }} />}
          onClick={handleClearAll}
          data-testid="rules-clear-all"
        >
          {translate("clear_all")}
        </HVButton>
        <HVButton
          variant="Primary"
          onClick={handleRulesOK}
          data-testid="rules-ok"
          styles={{ root: { minWidth: "80px" } }}
        >
          {translate("ok")}
        </HVButton>
      </S.BottomWrap>
    </S.Container>
  );
};
